import { CdkDropListGroup, CdkDropList, CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { DisplayedColumn } from 'src/app/core/graphql/generated/gen-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';

export function validDisplayedColumns(displayedColumns: DisplayedColumn[], displayedColumnsDefault: DisplayedColumn[]) {
	if (!displayedColumns || displayedColumns.length === 0) {
		return false;
	}
	if (displayedColumns.length !== displayedColumnsDefault.length) {
		return false;
	}
	// check if same lenght and same name, and disabled attributes, regardless of order and active
	// if not, return displayedColumnsDefault
	let valid = true;
	displayedColumns.forEach((col) => {
		if (!displayedColumnsDefault.find((column) => column.name === col.name && column.disabled === col.disabled)) {
			valid = false;
		}
	});
	return valid;
}

@Component({
	selector: 'app-update-displayed-columns',
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		MatIconModule,
		MatCheckboxModule,
		MatDividerModule,
		MatCardModule,
		MatTooltipModule,
		TranslateModule,
		CdkDropListGroup,
		CdkDropList,
		CdkDrag,
	],
	templateUrl: './update-displayed-columns.component.html',
	styleUrl: './update-displayed-columns.component.scss',
})
export class UpdateDisplayedColumnsComponent implements OnDestroy {
	displayedColumnsId: string;
	displayedColumns: DisplayedColumn[];
	displayedColumnsDefault: DisplayedColumn[];
	editableColumns: DisplayedColumn[];
	validCustom: boolean;
	private destroy$ = new Subject<void>();

	constructor(
		private tenantSettingService: TenantSettingService,
		public dialogRef: MatDialogRef<UpdateDisplayedColumnsComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
	) {
		this.displayedColumnsId = data.displayedColumnsId;
		this.tenantSettingService.getTenantSetting().valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
			next: (tenantSettings) => {
				// Get the displayedColumns from the tenant settings
				this.displayedColumnsDefault =
					tenantSettings.data.tenantSetting.operatorSettings.displayedColumns[`${this.displayedColumnsId}Default`];
				this.displayedColumns = tenantSettings.data.tenantSetting.operatorSettings.displayedColumns[this.displayedColumnsId];
				this.validCustom = validDisplayedColumns(this.displayedColumns, this.displayedColumnsDefault);
				this.displayedColumns = this.validCustom ? this.displayedColumns : this.displayedColumnsDefault;
				this.editableColumns = this.initEditableColumns(this.displayedColumns);
			},
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	drop(event: CdkDragDrop<string[]>) {
		moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
	}

	initEditableColumns(displayedColumns: DisplayedColumn[]): DisplayedColumn[] {
		// Create a deep copy of the input array to avoid modifying the original
		return displayedColumns.map((col) => ({
			name: col.name,
			active: col.active,
			mobile: col.mobile,
			disabled: col.disabled,
		}));
	}

	toggleColumnActive(column: DisplayedColumn) {
		column.active = !column.active;
	}

	save() {
		this.tenantSettingService.updateTenantOperatorSettingsDisplayedColumns({ [this.displayedColumnsId]: this.editableColumns }).subscribe({
			next: () => this.dialogRef.close(),
		});
	}

	cancel() {
		this.dialogRef.close();
	}
	reset() {
		this.editableColumns = this.initEditableColumns(this.displayedColumnsDefault);
	}
}
